import React, { useEffect, useMemo, useState } from 'react'
import withSlideUpModal from '../../../utils/hoc/withSlideUpModal';
import { CTA, GigStepProperty, GigStepPropertyWithValue, Milestone, MilestoneStep, MilestonSpecifications } from '../../../types/collaborations/CollaborationTypes';

import Choice from './form/Choice';
import { useParams } from 'react-router-dom';
import { getSignedGCSUploadURL, linkGCSFileWithMilestoneStep, submitMilestoneStep, uploadLargeFileInChunks } from '../../../services/api/collaboration';
import FileInput from './form/FileInput';
import WYSIWYG from './form/WYSIWYG';
import { toast } from 'react-toastify';
import CaptionInput from './components/CaptionInput';
import * as Sentry from "@sentry/react";
import { store } from '../../../redux-stores/store';
import { apiConfigs } from '../../../constants/apiConfigs';
import { debug } from '../../../services/api/auth';
interface FormBuilderProps {
    title: string,
    isActive: boolean,
    onClose: (refetch?: boolean) => void,
    activeMilestone: Milestone,
    step: MilestoneStep,
    disableBackdropClick?: (disable: boolean) => void
}



const FormBuilder: React.FC<FormBuilderProps> = ({ activeMilestone, step, onClose, disableBackdropClick }) => {


    const [properties, setProperties] = useState<GigStepPropertyWithValue[]>([]);

    const { campaignId } = useParams();

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        let _properties = Array.isArray(step?.gig_step?.properties) ? [...step.gig_step.properties] : [];

        _properties = _properties.sort((a, b) => a.sort_order - b.sort_order);


        setProperties(_properties);
    }, [step])

    useEffect(() => {
        if (disableBackdropClick)
            disableBackdropClick(submitting)
    }, [submitting])


    const getChoiceOptions = (property: GigStepProperty) => {
        let options: string[] = [];
        if (property.choices?.property && activeMilestone) {
            const properties = property.choices.property.split(".");
            if (properties.length > 0) {
                switch (properties[0]) {
                    case "milestone_specifications":
                        {
                            if (activeMilestone.specifications.hasOwnProperty(properties[1]) && activeMilestone.specifications[properties[1] as keyof MilestonSpecifications]) {
                                options = activeMilestone.specifications[properties[1] as keyof MilestonSpecifications] as string[];
                            }
                            break;
                        }
                }
            }
        }

        return options;
    }

    const handleChange = (index: number, value: string | boolean | File | CTA) => {
        let updated_properties = [...properties];
        updated_properties[index] = { ...updated_properties[index], value };
        setProperties(updated_properties);
    }

    const isValid = () => {
        for (let property of properties) {
            if (property.mandatory && !property.value) return false;
        }

        return true;
    }

    const validateForm = () => {
        let hasErrors = false
        let updated_properties = [...properties]
        for (let i in updated_properties) {
            if (updated_properties[i].mandatory && !updated_properties[i].value) {
                updated_properties[i] = { ...updated_properties[i], error: `${updated_properties[i].label} is required.` }
                hasErrors = true;
            }
        }

        if (hasErrors) {
            setProperties(updated_properties);
        }

        return hasErrors;
    }

    const setLargeFileUploadProgress = (propertyKey: string, progress: number) => {
        let _properties = properties.map((property) => {
            if (property.key === propertyKey) {
                return { ...property, progress }
            }
            return property;
        })

        setProperties(_properties);
    }

    const onSubmit = async () => {
        const isValid = !(validateForm());
        if (!isValid || !campaignId) return;

        setSubmitting(true);

        let data: any = {};

        for (let property of properties) {
            if (property.type === 'choice' && typeof property.value !== 'string' && typeof property.value === 'object') {
                for (let [key, value] of Object.entries(property.value)) {
                    data[`${property.key}[${key}]`] = value;
                }
            } else {
                data[property.key] = property.value;
            }
            if (property.type === 'large_file' && property.value instanceof File) {
                const file = property.value;
                const content_type = file.type;
                const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
                const signedGCSData = await getSignedGCSUploadURL(campaignId, { extension, content_type });
                await uploadLargeFileInChunks(file, signedGCSData.upload_url, (progress) => setLargeFileUploadProgress(property.key, progress.percentage));
                await linkGCSFileWithMilestoneStep(campaignId, { uuid: signedGCSData.uuid, extension, content_type, step: step.gig_step.slug });
                data[property.key] = signedGCSData.uuid;
            }
        }

        let startTime = performance.now();
        let responseTime: number | null = null;

        try {
            await submitMilestoneStep(campaignId, { step: step.gig_step.slug, ...data });
            onClose(true);
        } catch (error: any) {
            const userId = store.getState()?.auth?.user?.uuid;
            const apiErrorData = error?.response?.data;
            responseTime = performance.now() - startTime; // ms

            // Capture in Sentry with custom metadata
            Sentry.captureException(error, (scope) => {
                scope.setExtras({
                    userId,
                    apiError: apiErrorData,
                    apiEndpoint: apiConfigs.SUBMIT_COLLABORATION_MILESTONE_STEP.url.replace("<campaignId>", campaignId),
                    responseTimeMs: responseTime ?? "N/A",
                    requestPayload: { step: step.gig_step.slug, ...data }
                });
                scope.setTag("feature", "milestoneStepSubmission");
                scope.setLevel("error");
                return scope;
            });
            debug({ userId, apiErrorData, endpoint: apiConfigs.SUBMIT_COLLABORATION_MILESTONE_STEP.url.replace("<campaignId>", campaignId), responseTime, payload: { step: step.gig_step.slug, ...data } });
            toast.error(apiErrorData?.message ?? "Unknown error occurred.");
        } finally {
            setSubmitting(false);
        }
    }

    const getAcceptedFileTypes = (property: GigStepProperty) => {
        let accepted: any = {};

        if (Array.isArray(property.attributes.accepted)) {
            for (let acceptedFileType of property.attributes.accepted) {
                accepted[acceptedFileType] = []
            }
        }

        return accepted;
    }

    return (
        <div className='form-builder-form'>
            {
                properties?.map((property, i) => {
                    return (
                        <div key={i} className='form-builder-field'>
                            {
                                !['boolean', 'choice', 'wysiwyg', 'checkbox'].includes(property.type) &&
                                <label className='form-builder-label' htmlFor={`form-field-${i}`}>{property.label} {property.mandatory && <span className="text-danger">*</span>}</label>
                            }
                            {
                                property.type === 'checkbox' &&
                                <div className='d-flex align-items-center'>
                                    <div className="gig-selection-checkbox-wrapper me-1">
                                        <input type='checkbox' id='acknowledgement' className='form-check-input gig-selection-check-input' checked={typeof property.value === 'boolean' ? property.value : false} onChange={(e) => handleChange(i, e.target.checked)} />
                                    </div>
                                    <label className='acknowledgement-check-label' htmlFor={`form-field-${i}`}>{property.label} {property.mandatory && <span className="text-danger">*</span>}</label>
                                </div>
                            }
                            {
                                property.type === 'choice' &&
                                <Choice options={getChoiceOptions(property)} selected={property.value} onChange={(value) => handleChange(i, value)} />
                            }
                            {
                                property.type === 'text' &&
                                <input type='text' className='form-control form-builder-input' value={typeof property.value === 'string' ? property.value : ''} onChange={(e) => handleChange(i, e.target.value)} />
                            }
                            {
                                property.type === 'textarea' &&
                                <textarea rows={4} className='form-control form-builder-textarea' value={typeof property.value === 'string' ? property.value : ''} onChange={(e) => handleChange(i, e.target.value)}> </textarea>
                            }
                            {
                                property.type === 'caption' &&
                                <CaptionInput value={typeof property.value === 'string' ? property.value : ''} onChange={(text) => handleChange(i, text)} hashtags={activeMilestone?.specifications?.hashtags ?? []} mentions={activeMilestone?.specifications?.mentions ?? []} />
                            }
                            {
                                property.type === 'number' &&
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    className="form-control form-builder-input"
                                    value={typeof property.value === 'string' ? property.value : ''}
                                    onChange={(e) => {
                                        let cleaned = e.target.value.replace(/[^0-9.]/g, '');
                                        const parts = cleaned.split('.');
                                        if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
                                        handleChange(i, cleaned);
                                    }}
                                />
                            }
                            {
                                property.type === 'date' &&
                                <input type='date' className='form-control form-builder-input' value={typeof property.value === 'string' ? property.value : ''} onChange={(e) => handleChange(i, e.target.value)} />
                            }
                            {
                                property.type === 'datetime' &&
                                <input type='datetime-local' className='form-control form-builder-input' value={typeof property.value === 'string' ? property.value : ''} onChange={(e) => handleChange(i, e.target.value)} />
                            }
                            {
                                ['file', 'large_file'].includes(property.type) &&
                                <FileInput onChange={(file) => handleChange(i, file)} capture={property.attributes?.capture ?? null} accept={getAcceptedFileTypes(property)} maxSize={property.attributes.max_file_size} minSize={property.attributes.min_file_size} progress={property.progress} />
                            }
                            {
                                property.type === 'wysiwyg' &&
                                <React.Fragment>
                                    <label className='form-builder-label' htmlFor={`form-field-${i}`}> Add script for reel, post, story {property.mandatory && <span className="text-danger">*</span>}</label>
                                    <WYSIWYG defaultValue={typeof property.value === 'string' ? property.value : ''} onChange={(val) => handleChange(i, val)} />
                                </React.Fragment>
                            }
                            {
                                property.error &&
                                <span className="text-danger">{property.error}</span>
                            }
                        </div>
                    )
                })
            }
            <div className="milestone-step-actions">
                <button className="btn btn-outline-primary" onClick={() => !submitting && onClose()}>Cancel</button>
                <button className="btn btn-primary" disabled={!isValid() || submitting} onClick={onSubmit}>{submitting ? 'Submitting...' : 'Submit'}</button>
            </div>
        </div>
    )
}

const FormBuilderModal: React.FC<FormBuilderProps> = (props) => {
    const { title } = props;
    const [backdropClickDisabled, disableBackdropClick] = useState(false);

    const ModalComponent = useMemo(() => withSlideUpModal(FormBuilder, title), [title])

    return <ModalComponent {...props} backdropClickDisabled={backdropClickDisabled} disableBackdropClick={disableBackdropClick} />
}

export default FormBuilderModal;